import { axisBottom, BaseType, format, select, Selection } from "d3";
import { fraction } from "mathjs";

import { generateTicksFromAxis } from "@examspark/common/dist/utils/number-line";
import { Cartesian } from "@examspark/types";

import { D3NumberLine } from "../d3Utils";

const isLabelledTick = (index: number, axis: Cartesian.NumberLine) => {
  return axis.useMinorTickLabels || index % ((axis.minorTicks ?? 0) + 1) === 0;
};

const isUnlabeledTick = (...props: Parameters<typeof isLabelledTick>) =>
  !isLabelledTick(...props);

export const useGraphDecorator =
  ({
    axis,
    margin,
  }: {
    axis: D3NumberLine;
    margin: { top: number; right: number; bottom: number; left: number };
  }) =>
  <GType extends BaseType>(svg: Selection<GType, unknown, any, any>) => {
    const graph = svg
      .selectAll("g.graph")
      .data([axis])
      .join("g")
      .attr("class", "graph")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    const minorTickLength = 5;
    const majorTickLength = 8;

    const axisDecorator = axisBottom(axis.scale)
      .tickValues(generateTicksFromAxis(axis))
      .offset(0)
      .tickPadding(9)
      .tickFormat((domainValue, index) => {
        if (isUnlabeledTick(index, axis)) return "";

        if (axis.precision != null && axis.precision >= 0) {
          let stringified = format(`.${axis.precision}f`)(+domainValue);
          if (axis.tickLabel) {
            stringified += axis.tickLabel.substring(0, 3);
          }
          return stringified;
        } else {
          // This logic was the original 'smart' code. It was replaced with
          // user choice. Good.

          const minorTicks = axis.minorTicks ?? 0;
          const tickDistance = axis.tickDistance ?? 1;

          // this isn't perfect, especially with number > 1 with a decimal
          const tickDistanceDecimal = tickDistance % 1;
          const precisionFromTickDistance = tickDistanceDecimal
            ? tickDistanceDecimal ** -1
            : 0;
          const precisionFromMinorTicks =
            axis.useMinorTickLabels && minorTicks ? minorTicks + 1 : 0;

          // include enough decimals to distinguish between ticks
          // for examle, showing 1/8 (.125) requires 3 decimals, which is log2(8)
          // .33 is 1/3, is 2 decimals, which is log2(3) rounded up.
          let precision = 0;
          if (precisionFromMinorTicks || precisionFromTickDistance) {
            precision = Math.ceil(
              Math.log2(precisionFromMinorTicks + precisionFromTickDistance),
            );
          }

          return format(`.${precision}f`)(+domainValue);
        }
      })
      .tickSizeOuter(0);

    // axis line
    graph
      .selectAll<SVGGElement, number>("g.axis")
      .data([axis])
      .join<any>("g")
      .call(axisDecorator)
      .attr("class", "axis")
      .style("font", "1.30rem sans-serif")
      .select(".domain")
      .attr("stroke-width", 2)
      .attr("stroke", "#696969")
      .style("marker-end", "url(#arrow-with-tail)")
      .style("marker-start", "url(#arrow-with-tail)");

    // stretch ticks above the axis
    graph
      .selectAll<SVGLineElement, number>("g.tick line")
      .attr("stroke-width", 2)
      .style("shape-rendering", "crispedges")
      .attr("stroke", "#696969")
      .attr("y1", (d, i) =>
        isLabelledTick(i, axis) ? -majorTickLength : -minorTickLength,
      )
      .attr("y2", (d, i) =>
        isLabelledTick(i, axis) ? majorTickLength : minorTickLength,
      );

    // convert decimals to fractions if toggled

    const ticks = graph.selectAll<SVGTextElement, number>("g.tick text");

    // we need to precalculate the tex to get the width and height
    // of the largest tex element, so we can align the foreignObject
    // elements centered
    const to_fractions =
      axis.labelMode === "fraction" || axis.labelMode === "mixed_fraction";

    const gcd = axis.useCommonDenominator
      ? generateTicksFromAxis(axis)
          .filter((d, t) => isLabelledTick(t, axis))
          .map((d) => fraction(d).d)
          .reduce((prev, curr) => (curr > prev ? curr : prev), 0)
      : 0;
    const precalculated_tex = ticks
      .data()
      .filter((d, i) => to_fractions && isLabelledTick(i, axis))
      .reduce((acc, d) => {
        return acc.set(
          d,
          calculateTexFraction(d, {
            force_denominator: gcd,
            use_mixed_numbers: axis.labelMode === "mixed_fraction",
            tickLabel: axis.tickLabel,
          }),
        );
      }, new Map<number, SVGSVGElement>());

    const precalculated_tex_values = [...precalculated_tex.values()];

    const largest_tex = precalculated_tex_values.reduce(
      (prev, curr) => {
        const curr_width = curr.width.baseVal.valueInSpecifiedUnits;
        const prev_width = prev.width.baseVal.valueInSpecifiedUnits;
        const curr_height = curr.height.baseVal.valueInSpecifiedUnits;
        const prev_height = prev.height.baseVal.valueInSpecifiedUnits;

        return {
          width: curr_width > prev_width ? curr.width : prev.width,
          height: curr_height > prev_height ? curr.height : prev.height,
        };
      },
      precalculated_tex_values[0] as Pick<SVGSVGElement, "width" | "height">,
    );

    ticks.each(function (d, i) {
      const tick = select(this.parentElement);

      // rendering an empty data array will remove the element, hence why we use a single element array
      const data: any[] = [];
      if (precalculated_tex.has(d)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        data.push(precalculated_tex.get(d)!);
      }

      tick
        .selectAll("foreignObject")
        .data(data)
        .join("foreignObject")
        .attr("width", (tex) => largest_tex.width.baseVal.valueAsString)
        .attr("height", (tex) => largest_tex.height.baseVal.valueAsString)
        .attr("y", "1em")
        .attr(
          "x",
          (tex) =>
            `-${largest_tex.width.baseVal.valueInSpecifiedUnits / 2 + "ex"}`,
        )
        .html(
          (tex) => `
              <div style="width: 100%; height: 100%; display: flex; justify-content: center; align-items: center;">
                ${tex.outerHTML}
              </div>
              `,
        );

      tick.select("text").attr("opacity", to_fractions ? 0 : 1);
    });
  };

const calculateTexFraction = (
  d: number,
  {
    force_denominator,
    use_mixed_numbers,
    tickLabel,
  }: {
    force_denominator?: number;
    use_mixed_numbers: boolean;
    tickLabel?: string;
  },
): SVGSVGElement => {
  const MathJax = window["MathJax"];

  const frax = fraction(d);
  const scale = force_denominator ? force_denominator / frax.d : 1;

  let latex = d < 0 ? "-" : "";
  if (use_mixed_numbers) {
    const whole = Math.floor(frax.n / frax.d);
    frax.n -= whole * frax.d;

    // whole number
    if (whole !== 0) {
      latex += whole;
    }

    if (frax.n !== 0) {
      latex += `\\frac{${frax.n * scale}}{${frax.d * scale}}`;
    }

    // backup, since we'd otherwise miss rendering 0
    if (!latex) {
      latex = "0";
    }
  } else {
    // rendering fractions with a denominator of 1 is silly
    if (frax.d === 0 || frax.d === 1) {
      latex += frax.n;
    } else {
      // latex = `\\frac{${frax.n}}{${frax.d}}`;

      if (frax.n !== 0) {
        latex += `\\frac{${frax.n * scale}}{${frax.d * scale}}`;
      }
    }
  }

  if (tickLabel) {
    latex += tickLabel;
  }

  return MathJax.tex2svg(latex).firstChild;
};
